import React from "react";
import { Edit, Clock, BookOpen, Plane, AlertTriangle } from "lucide-react";
import * as Tooltip from '@radix-ui/react-tooltip';

// Component displaying a single event
const EventItem = ({ item, userId, date, onDoubleClick, onEditTraining, qualificationTypes = [], trainings = [] }) => {
  const isTraining = item.type === "training";
  const isTravel = item.type === "travel";
  const isQualificationAlert = item.type === "qualificationalert";

  const bgClass = isQualificationAlert
    ? (item.daysRemaining < 0
      ? "bg-red-200 border-red-400"
      : item.daysRemaining <= 30
        ? "bg-orange-100 border-orange-400"
        : item.daysRemaining <= 60
          ? "bg-yellow-100 border-yellow-400"
          : "bg-green-100 border-green-400")
    : isTraining
        ? "bg-brand-200 border-brand-400"
        : "bg-green-200 border-green-400";

  const borderColor = isQualificationAlert
    ? (item.daysRemaining < 0
      ? "bg-red-600"
      : item.daysRemaining <= 30
        ? "bg-orange-600"
        : item.daysRemaining <= 60
          ? "bg-yellow-600"
          : "bg-green-600")
    : isTraining
        ? "bg-brand-500"
        : "bg-green-500";

  const EventIcon = isTraining ? BookOpen : isTravel ? Plane : AlertTriangle;

  const title = isTraining
      ? (item.trainingName || item.training?.name || item.description || "Unnamed training")
      : isTravel
        ? (item.address || item.description || "Unnamed travel")
        : (item.trainingName || "Qualification expiration");

  // Generate tooltip content for training items
  const getTooltipContent = () => {
    if (isTraining) {
      
      const trainingName = item.trainingName || item.description || 'Unnamed training';
      const qualificationName = item.qualificationName || 'Unknown qualification';
      
      // Try to find company training number from qualificationTypes
      let companyTrainingNumber = null;
      if (qualificationTypes && qualificationTypes.length > 0) {
        const qualType = qualificationTypes.find(qt => 
          qt.name === qualificationName && qt.company_data
        );
        if (qualType && qualType.company_data && qualType.company_data.training_number) {
          companyTrainingNumber = qualType.company_data.training_number;
        }
      }
      
      // Get participants info - try from item first, then from trainings array
      let participantsInfo = '';
      
      // Try to get data directly from item (simpler approach)
      if (item.participants_count !== undefined || item.availability !== undefined) {
        const participantsCount = item.participants_count || 0;
        const maxParticipants = item.availability;
        
        if (maxParticipants) {
          participantsInfo = `\n    Participants: ${participantsCount}/${maxParticipants}`;
        } else {
          participantsInfo = `\n    Participants: ${participantsCount}`;
        }
      } 
      // Fallback to trainings array lookup
      else if (item.originalTrainingId && trainings && trainings.length > 0) {
        const originalTraining = trainings.find(t => t.id === item.originalTrainingId);
        if (originalTraining) {
          const participantsCount = originalTraining.participants_count || (originalTraining.users_data ? originalTraining.users_data.length : 0);
          const maxParticipants = originalTraining.availability || originalTraining.training_center_qualification_data?.availability;
          
          if (maxParticipants) {
            participantsInfo = `\n    Participants: ${participantsCount}/${maxParticipants}`;
          } else {
            participantsInfo = `\n    Participants: ${participantsCount}`;
          }
        }
      }
      
      const status = item.status || 'planned';
      
      let tooltipContent = `• ${trainingName}\n    Qualification: ${qualificationName}`;
      if (companyTrainingNumber) {
        tooltipContent += `\n    Training No: ${companyTrainingNumber}`;
      }
      tooltipContent += participantsInfo;
      tooltipContent += `\n    Status: ${status}`;
      
      
      return {
        title: trainingName,
        content: tooltipContent
      };
    } else if (isQualificationAlert) {
      const daysText = item.daysRemaining < 0 
        ? `expired ${Math.abs(item.daysRemaining)} days ago`
        : `expires in ${item.daysRemaining} days`;
      
      return {
        title: `Qualification ${daysText}`,
        content: `• ${item.trainingName || 'Qualification'}`
      };
    }
    return null;
  };

  const tooltipData = getTooltipContent();
  

  const eventContent = (
    <div
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (isQualificationAlert) {
          onDoubleClick(item, { openQualification: true, qualificationId: item.qualificationId });
        } else {
          onDoubleClick(item);
        }
      }}
      className={`p-1 rounded text-[9px] border shadow-sm ${bgClass} hover:shadow-md transition-shadow relative overflow-hidden cursor-pointer`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${borderColor}`}></div>
      <div className="pl-1">
        <div className="font-medium text-[9px] mb-0.5 flex items-center justify-between">
          <div className="flex items-center overflow-hidden">
            {isQualificationAlert && item.daysRemaining <= 60 ? (
              <AlertTriangle
                className={`h-2 w-2 mr-0.5 flex-shrink-0 ${item.daysRemaining < 0 ? "text-red-600" :
                  item.daysRemaining <= 30 ? "text-orange-600" :
                    "text-yellow-600"
                  }`}
              />
            ) : (
              !isQualificationAlert && (
                <EventIcon
                  className={`h-2 w-2 mr-0.5 flex-shrink-0 ${isTraining ? "text-brand-500" :
                      isTravel ? "text-green-600" :
                        "text-green-600"
                    }`}
                />
              )
            )}
            <span className="truncate">{title}</span>
          </div>
          <div className="flex items-center">
            {isTraining && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('EventItem Edit button clicked, item:', item);
                  onEditTraining(item);
                }}
                className="ml-0.5 p-0.5 rounded hover:bg-brand-300 text-brand-600"
                title="Edit training"
              >
                <Edit className="h-2 w-2" />
              </button>
            )}
          </div>
        </div>

        {!isQualificationAlert && (
          <div className="text-[8px] text-slate-500 truncate">
            {isTraining ? (item.training_center_name || item.location || "Training Center") : (item.location || "No location")}
          </div>
        )}
        {isQualificationAlert && (
          <div className="text-[8px] font-medium mt-0.5">
            <span
              className={
                item.daysRemaining < 0 ? "text-red-600" :
                  item.daysRemaining <= 30 ? "text-orange-600" :
                    item.daysRemaining <= 60 ? "text-yellow-600" :
                      "text-green-600"
              }
            >
              {item.daysRemaining < 0
                ? `Expired: ${new Date(item.expiryDate).toLocaleDateString()}`
                : `Expires: ${new Date(item.expiryDate).toLocaleDateString()}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  // Wrap with tooltip for training and qualification items
  if (tooltipData && (isTraining || isQualificationAlert)) {
    return (
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          {eventContent}
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="bg-slate-800 text-white text-xs rounded-xl p-3 shadow-lg max-w-md z-50"
            side="right"
            sideOffset={5}
          >
            <div className="whitespace-pre">
              <strong>{tooltipData.title}</strong>
              {tooltipData.content && (
                <>
                  {'\n\n'}{tooltipData.content}
                </>
              )}
            </div>
            <Tooltip.Arrow className="fill-slate-800" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    );
  }

  return eventContent;
};

export default EventItem;